import {
  afterNextRender,
  ChangeDetectorRef,
  Component,
  DOCUMENT,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  Output,
  PLATFORM_ID,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { TranslatePipe } from '@app/pipes/translate.pipe';
import { IconComponent } from '@app/components/ui-icon/icon.component';
import { type IconName } from '@app/components/ui-icon/icon-registry';

export interface DeepLinkButton {
  label: string;
  action: () => void;
  show?: boolean;
  icon?: IconName;
  /** When true the tab never gets the active highlight (e.g. it opens a modal). */
  noHighlight?: boolean;
}

@Component({
  imports: [CommonModule, TranslatePipe, IconComponent],
  providers: [],
  selector: 'app-deep-link-buttons',
  templateUrl: './deep-link-buttons.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./deep-link-buttons.component.scss'],
})
export class DeepLinkButtonsComponent implements OnInit, OnChanges, OnDestroy {
  @Input() buttons: DeepLinkButton[] = [];
  @Input() showScrollArrows: boolean = true;
  @Input() containerClass: string = '';
  @Input() roundedCorners: boolean = false;
  @Output() buttonClick: EventEmitter<string> = new EventEmitter();

  /** Currently highlighted tab. Starts as null (no default selection). */
  activeButton: DeepLinkButton | null = null;

  @ViewChild('deepLinkButtons') deepLinkButtonsRef!: ElementRef<HTMLDivElement>;

  // Scroll arrows state
  showLeftArrow = false;
  showRightArrow = false;

  // Resize handler for cleanup
  private resizeHandler = () => {
    requestAnimationFrame(() => this.checkScrollArrows());
  };

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    @Inject(DOCUMENT) private document: Document,
    private cdr: ChangeDetectorRef,
  ) {
    afterNextRender(() => {
      if (isPlatformBrowser(this.platformId)) {
        requestAnimationFrame(() => this.checkScrollArrows());
      }
    });
  }

  ngOnInit(): void {
    this.syncActiveButton();

    // Check scroll arrows after content is loaded with single attempt
    if (isPlatformBrowser(this.platformId)) {
      requestAnimationFrame(() => {
        this.checkScrollArrows();
      });
    }

    // Add window resize listener for responsive behavior
    if (isPlatformBrowser(this.platformId)) {
      window.addEventListener('resize', this.resizeHandler);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['buttons']) {
      this.syncActiveButton();
    }
  }

  ngOnDestroy(): void {
    // Remove resize listener
    if (isPlatformBrowser(this.platformId)) {
      window.removeEventListener('resize', this.resizeHandler);
    }
  }

  onButtonClick(button: DeepLinkButton): void {
    this.buttonClick.emit(button.label);
    if (!button.noHighlight) {
      this.activeButton = button;
    }
    button.action();
  }

  checkScrollArrows(): void {
    if (
      !this.showScrollArrows ||
      !isPlatformBrowser(this.platformId) ||
      !this.deepLinkButtonsRef
    ) {
      return;
    }

    const container = this.deepLinkButtonsRef.nativeElement;
    const scrollLeft = container.scrollLeft;
    const scrollWidth = container.scrollWidth;
    const clientWidth = container.clientWidth;

    // Only proceed if the container has proper dimensions
    if (scrollWidth === 0 || clientWidth === 0) {
      // Retry after a short delay if dimensions aren't ready
      if (isPlatformBrowser(this.platformId)) {
        requestAnimationFrame(() => this.checkScrollArrows());
      }
      return;
    }

    this.showLeftArrow = scrollLeft > 0;
    this.showRightArrow = scrollLeft < scrollWidth - clientWidth - 1; // -1 for rounding errors
    this.cdr.markForCheck();
  }

  scrollLeft(): void {
    if (!isPlatformBrowser(this.platformId) || !this.deepLinkButtonsRef) {
      return;
    }

    const container = this.deepLinkButtonsRef.nativeElement;
    const scrollAmount = 200; // Adjust scroll distance as needed
    container.scrollBy({
      left: -scrollAmount,
      behavior: 'smooth',
    });

    // Check arrows after scroll animation
    if (isPlatformBrowser(this.platformId)) {
      requestAnimationFrame(() => this.checkScrollArrows());
    }
  }

  scrollRight(): void {
    if (!isPlatformBrowser(this.platformId) || !this.deepLinkButtonsRef) {
      return;
    }

    const container = this.deepLinkButtonsRef.nativeElement;
    const scrollAmount = 200; // Adjust scroll distance as needed
    container.scrollBy({
      left: scrollAmount,
      behavior: 'smooth',
    });

    // Check arrows after scroll animation
    if (isPlatformBrowser(this.platformId)) {
      requestAnimationFrame(() => this.checkScrollArrows());
    }
  }

  // Public method to force check scroll arrows (useful for debugging)
  public forceCheckScrollArrows(): void {
    this.checkScrollArrows();
  }

  // Helper method to check if button should be shown
  shouldShowButton(button: DeepLinkButton): boolean {
    return button.show !== false;
  }

  private syncActiveButton(): void {
    const currentLabel = this.activeButton?.label;
    if (!currentLabel) {
      // No button was previously active — don't auto-select a default.
      return;
    }

    const match = this.buttons.find(
      (button) =>
        button.label === currentLabel &&
        this.shouldShowButton(button) &&
        !button.noHighlight,
    );
    // Keep the active button if it still exists, otherwise clear it.
    this.activeButton = match ?? null;
  }
}
